import React, { useEffect, useState } from 'react';
import tenantRoutes from '@routes';
import { Tooltip } from 'antd';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useRouteNavigate } from '../../../../hooks';
import { EmptyState } from '../../../../components/common';
import { formatNumberString } from '../../../../utility/utility';
import {
  adLicenseWidgetImpressionEvent,
  adLicenseClickEvent,
} from '../../../../services/analyticsService';
import { PAGE_SECTIONS, INTERACTED_FROM } from '../../../../services/analyticsService/constants';
import {
  POST_LISTING_STEP_PARAM,
  POST_LISTING_STEPS,
} from '../../../../components/post-listing-menu/post-listing-options';
import { useGetRegaAdLicensesSummaryQuery } from '../../apis/regaAdLicenses';
import { getCardState, getSummaryUserId, redirectToVerifyFal } from './utils';
import { AdLicenseAnimations, SkeletonBlock } from './styled';
import { InfoCircleIcon, ChevronRightIcon, LicenseBannerIcon, AnimatedCheckIcon, LockIcon } from './icons';
import AssistanceStatus from './assistance-status';
import RequestAssistanceModal from './request-assistance-modal';
import {
  MetricLabelRoot,
  MetricLabelText,
  MetricTooltipTrigger,
  CardShell,
  CardTitleRow,
  CardTitleText,
  GreenCtaButton,
  Divider,
  ContentRow,
  LeftCluster,
  BannerIconWrap,
  MetricGroup,
  MetricColumn,
  MetricValue,
  NotPostedLabel,
  ClearValueRow,
  TickRing,
  TickHalo,
  BlurRow,
  BlurredValue,
  LockIconWrap,
  StepsColumn,
  StepsHeading,
  StepsRow,
  StepItemActive,
  StepBadgeActive,
  ChevronWrap,
  StepItemInactive,
  StepBadgeInactive,
  ActionCluster,
} from './active-ad-licenses-widget.styled';

// Passed to antd <Tooltip overlayInnerStyle={...}> as a prop, so it must stay a plain
// style object (not a styled-component).
const TOOLTIP_INNER_STYLE = {
  width: 240,
  padding: '9px 12px',
  background: '#fff',
  border: '1px solid #E8EBF0',
  borderRadius: 9,
  boxShadow: 'rgba(16,24,40,0.16) 0px 10px 26px',
  color: 'rgb(85,85,85)',
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 1.5,
};

const MetricLabel = ({ label, tooltip, isMobile = false, nowrap = false }) => (
  <MetricLabelRoot $nowrap={nowrap} $isMobile={isMobile}>
    <MetricLabelText $nowrap={nowrap} $isMobile={isMobile}>
      {label}
    </MetricLabelText>
    {tooltip ? (
      <Tooltip
        title={tooltip}
        overlayInnerStyle={TOOLTIP_INNER_STYLE}
        color="#fff"
        placement="top"
        getPopupContainer={() => document.body}
      >
        <MetricTooltipTrigger>
          <InfoCircleIcon size={14} />
        </MetricTooltipTrigger>
      </Tooltip>
    ) : null}
  </MetricLabelRoot>
);

const CardShellDiv = ({ children }) => <CardShell>{children}</CardShell>;

const CardTitle = ({ t }) => (
  <CardTitleRow>
    <CardTitleText>{t('Active Ad Licenses')}</CardTitleText>
  </CardTitleRow>
);

const GreenCta = ({ label, onClick }) => (
  <GreenCtaButton type="button" onClick={onClick}>
    {label}
  </GreenCtaButton>
);

const ActiveAdLicensesWidget = ({ selectedUser }) => {
  const { t } = useTranslation();
  const navigate = useRouteNavigate();
  const isMobile = useSelector((state) => state.app.AppConfig.isMobile);
  const rtl = useSelector((state) => state.app.AppConfig.rtl);
  const { user: loginUser } = useSelector((state) => state.app.loginUser);
  const [assistOpen, setAssistOpen] = useState(false);

  // On mount (and the "(Agency)" aggregate) the summary is fetched with no params; selecting a
  // specific user refetches it scoped with user_id (see getSummaryUserId).
  const {
    data: summary,
    isLoading,
    isError,
    refetch,
  } = useGetRegaAdLicensesSummaryQuery(
    { userId: getSummaryUserId(loginUser, selectedUser) },
    { skip: !selectedUser?.id, refetchOnMountOrArgChange: true },
  );

  const cardState = summary ? getCardState(summary) : null;

  useEffect(() => {
    if (!cardState) return;
    const value =
      cardState === 'pending' ? 'not_posted' : cardState === 'clear' ? 'all_posted' : 'fal_unverified';
    adLicenseWidgetImpressionEvent(loginUser, value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardState]);

  const goPostListing = () => {
    adLicenseClickEvent(loginUser, { pageSection: PAGE_SECTIONS.AD_LICENSE_WIDGET, value: summary?.unposted_count });
    navigate(
      `${tenantRoutes.app().post_listing.path}?${POST_LISTING_STEP_PARAM}=${POST_LISTING_STEPS.LICENSE_NUMBER}`,
    );
  };
  const goRequestLicense = () => {
    adLicenseClickEvent(loginUser, { pageSection: PAGE_SECTIONS.AD_LICENSE_WIDGET, value: 'all_posted' });
    navigate(tenantRoutes.app().ad_license.path);
  };
  const goVerifyFal = () => {
    adLicenseClickEvent(loginUser, { pageSection: PAGE_SECTIONS.AD_LICENSE_WIDGET, value: 'fal_unverified' });
    redirectToVerifyFal();
  };
  const goAssist = () => {
    adLicenseClickEvent(loginUser, {
      pageSection: PAGE_SECTIONS.ASSISTANCE,
      interactedFrom: INTERACTED_FROM.DASHBOARD_WIDGET,
    });
    setAssistOpen(true);
  };

  if (isLoading) {
    return (
      <CardShellDiv>
        <SkeletonBlock width="160px" height="18px" />
        <SkeletonBlock height="56px" />
      </CardShellDiv>
    );
  }

  if (isError || !summary) {
    return (
      <CardShellDiv>
        <EmptyState type="analytics-dashboard" message={t('Something went wrong')} onClick={refetch} />
      </CardShellDiv>
    );
  }

  const divider = <Divider />;

  return (
    <>
      <AdLicenseAnimations />
      <CardShellDiv>
        <CardTitle t={t} />
        <ContentRow $isMobile={isMobile}>
          <LeftCluster $isMobile={isMobile}>
            <BannerIconWrap>
              <LicenseBannerIcon size={44} />
            </BannerIconWrap>

            {cardState === 'pending' && (
              <>
                <MetricGroup>
                  <MetricLabel
                    label={t('Active Ad Licenses not posted')}
                    tooltip={t('Ad licenses collected against your FAL with no listing posted yet.')}
                    isMobile={isMobile}
                  />
                  <MetricValue>{formatNumberString(summary.unposted_count)}</MetricValue>
                </MetricGroup>
                {divider}
                <MetricGroup>
                  <MetricLabel
                    label={t('Ready to Post')}
                    tooltip={t('Draft listings with every required field filled - one click from going live.')}
                    isMobile={isMobile}
                    nowrap
                  />
                  <MetricValue>{formatNumberString(summary.ready_to_post_count)}</MetricValue>
                </MetricGroup>
              </>
            )}

            {cardState === 'clear' && (
              <MetricColumn>
                <NotPostedLabel>{t('Active Ad Licenses not posted')}</NotPostedLabel>
                <ClearValueRow>
                  <MetricValue>0</MetricValue>
                  <TickRing>
                    <TickHalo />
                    <AnimatedCheckIcon size={15} />
                  </TickRing>
                </ClearValueRow>
              </MetricColumn>
            )}

            {cardState === 'unverified' && (
              <>
                <MetricColumn>
                  <MetricLabel
                    label={t('Active Ad Licenses not posted')}
                    tooltip={t('Ad licenses collected against your FAL with no listing posted yet.')}
                    isMobile={isMobile}
                  />
                  <BlurRow>
                    <BlurredValue>##</BlurredValue>
                    <LockIconWrap>
                      <LockIcon size={18} />
                    </LockIconWrap>
                  </BlurRow>
                </MetricColumn>
                {divider}
                <StepsColumn>
                  <StepsHeading>{t('Two steps to see your ad licenses')}</StepsHeading>
                  <StepsRow>
                    <StepItemActive>
                      <StepBadgeActive>1</StepBadgeActive>
                      {t('Verify FAL')}
                    </StepItemActive>
                    <ChevronWrap>
                      <ChevronRightIcon size={14} />
                    </ChevronWrap>
                    <StepItemInactive>
                      <StepBadgeInactive>2</StepBadgeInactive>
                      {t('View your active ad licenses')}
                    </StepItemInactive>
                  </StepsRow>
                </StepsColumn>
              </>
            )}
          </LeftCluster>

          <ActionCluster $isMobile={isMobile} $rtl={rtl}>
            {cardState === 'pending' && (
              <>
                <AssistanceStatus assistance={summary.assistance} onReachOut={goAssist} showChip />
                <GreenCta label={t('Start Posting')} onClick={goPostListing} />
              </>
            )}
            {cardState === 'clear' && <GreenCta label={t('Request an Ad License')} onClick={goRequestLicense} />}
            {cardState === 'unverified' && (
              <GreenCta label={t('Verify', { context: 'adLicense' })} onClick={goVerifyFal} />
            )}
          </ActionCluster>
        </ContentRow>
      </CardShellDiv>

      <RequestAssistanceModal
        visible={assistOpen}
        onClose={() => setAssistOpen(false)}
        unpostedCount={summary.unposted_count}
      />
    </>
  );
};

export default ActiveAdLicensesWidget;
